import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { MessagingProvider } from './contexts/MessagingContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ReviewProvider } from './contexts/ReviewContext';
import { MapProvider } from './contexts/MapContext';
import { PaymentProvider } from './contexts/PaymentContext';
import { VendorProvider } from './contexts/VendorContext';
import { UserActionsProvider } from './contexts/UserActionsContext';
import ShellPage from './pages/marketplace/ShellPage';
import RequireAuth from './components/auth/RequireAuth';
import { useNotificationAutomation } from './hooks/useNotificationAutomation';
import dwelloTheme from './theme/dwelloTheme';

// ── Lazy-loaded pages ─────────────────────────────────────────────────────────
// Each import() call is its own JS chunk — browsers only fetch what's needed.
const LoginPage                   = React.lazy(() => import('./pages/LoginPage'));
const MpHome                     = React.lazy(() => import('./pages/marketplace/HomePage'));
const MpSearch                   = React.lazy(() => import('./pages/marketplace/SearchPage'));
const MpProperty                 = React.lazy(() => import('./pages/marketplace/PropertyRoute'));
const MpRequest                  = React.lazy(() => import('./pages/marketplace/RequestToBookPage'));
const MpMyHome                   = React.lazy(() => import('./pages/marketplace/MyHomePage'));
const MapPage                     = React.lazy(() => import('./pages/MapPage'));
const MyOffersPage                = React.lazy(() => import('./pages/MyOffersPage'));
const FavoritesPage                = React.lazy(() => import('./pages/FavoritesPage'));
const ApplicationsPage             = React.lazy(() => import('./pages/ApplicationsPage'));
const LeasesPage                   = React.lazy(() => import('./pages/LeasesPage'));
const MyReviewsPage                = React.lazy(() => import('./pages/MyReviewsPage'));
const AgentDirectoryPage          = React.lazy(() => import('./pages/AgentDirectoryPage'));
const AgentProfilePage            = React.lazy(() => import('./pages/AgentProfilePage'));
const PurchaseAgreementPage       = React.lazy(() => import('./pages/PurchaseAgreementPage'));
const MessagesPage                = React.lazy(() => import('./pages/MessagesPage'));
const UserProfile                 = React.lazy(() => import('./components/profile/UserProfile'));
const PaymentsHistoryPage         = React.lazy(() => import('./pages/PaymentsHistoryPage'));
const PaymentPage                 = React.lazy(() => import('./pages/PaymentPage'));
const PaymentSuccessPage          = React.lazy(() => import('./pages/PaymentSuccessPage'));
const PaystackCallbackPage        = React.lazy(() => import('./pages/PaystackCallbackPage'));
const SettingsPage                = React.lazy(() => import('./pages/SettingsPage'));
const NotificationPreferencesPage = React.lazy(() => import('./pages/NotificationPreferencesPage'));
const NotificationCenter          = React.lazy(() => import('./components/notifications/NotificationCenter'));
const HelpSupportPage             = React.lazy(() => import('./pages/HelpSupportPage'));
const ForgotPasswordPage          = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage           = React.lazy(() => import('./pages/ResetPasswordPage'));
// Named-export pages — wrap so React.lazy gets a default export
const PrivacyPolicyPage           = React.lazy(() => import('./pages/LegalPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage          = React.lazy(() => import('./pages/LegalPage').then(m => ({ default: m.TermsOfServicePage })));
const NotFoundPage                = React.lazy(() => import('./pages/NotFoundPage'));
const InsightsPage                = React.lazy(() => import('./pages/InsightsPage'));

// ── Fallback shown while a chunk loads ───────────────────────────────────────
const PageLoader: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress size={40} />
  </Box>
);

function AppContent() {
  useNotificationAutomation();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes — browsing is never gated */}
        <Route path="/" element={<MpHome />} />
        <Route path="/search" element={<MpSearch />} />
        <Route path="/book/:propertyId" element={<RequireAuth><MpRequest /></RequireAuth>} />
        <Route path="/my-home" element={<MpMyHome />} />
        <Route path="/map" element={<ShellPage fullWidth><MapPage /></ShellPage>} />
        <Route path="/property/:id" element={<MpProperty />} />
        <Route path="/login" element={<ShellPage><LoginPage /></ShellPage>} />
        <Route path="/forgot-password" element={<ShellPage><ForgotPasswordPage /></ShellPage>} />
        <Route path="/reset-password/:token" element={<ShellPage><ResetPasswordPage /></ShellPage>} />
        <Route path="/privacy-policy" element={<ShellPage><PrivacyPolicyPage /></ShellPage>} />
        <Route path="/terms" element={<ShellPage><TermsOfServicePage /></ShellPage>} />
        <Route path="/help" element={<ShellPage><HelpSupportPage /></ShellPage>} />

        {/* Account actions — gated on "signed in," nothing more (this app has no roles) */}
        <Route path="/my-offers" element={
          <ShellPage><RequireAuth><MyOffersPage /></RequireAuth></ShellPage>
        } />
        <Route path="/favorites" element={
          <ShellPage><RequireAuth><FavoritesPage /></RequireAuth></ShellPage>
        } />
        <Route path="/applications" element={
          <ShellPage><RequireAuth><ApplicationsPage /></RequireAuth></ShellPage>
        } />
        <Route path="/leases" element={
          <ShellPage><RequireAuth><LeasesPage /></RequireAuth></ShellPage>
        } />
        <Route path="/my-reviews" element={
          <ShellPage><RequireAuth><MyReviewsPage /></RequireAuth></ShellPage>
        } />
        <Route path="/agents" element={
          <ShellPage><RequireAuth><AgentDirectoryPage /></RequireAuth></ShellPage>
        } />
        <Route path="/agents/:agentId" element={
          <ShellPage><RequireAuth><AgentProfilePage /></RequireAuth></ShellPage>
        } />
        <Route path="/purchase-agreements/:id" element={
          <ShellPage><RequireAuth><PurchaseAgreementPage /></RequireAuth></ShellPage>
        } />
        <Route path="/messages" element={
          <ShellPage><RequireAuth><MessagesPage /></RequireAuth></ShellPage>
        } />
        <Route path="/insights" element={
          <ShellPage><RequireAuth><InsightsPage /></RequireAuth></ShellPage>
        } />
        <Route path="/profile" element={
          <ShellPage><RequireAuth><UserProfile /></RequireAuth></ShellPage>
        } />
        <Route path="/settings" element={
          <ShellPage><RequireAuth><SettingsPage /></RequireAuth></ShellPage>
        } />
        <Route path="/notifications" element={
          <ShellPage><RequireAuth><NotificationCenter /></RequireAuth></ShellPage>
        } />
        <Route path="/notification-preferences" element={
          <ShellPage><RequireAuth><NotificationPreferencesPage /></RequireAuth></ShellPage>
        } />
        <Route path="/payments" element={
          <ShellPage fullWidth><RequireAuth><PaymentsHistoryPage /></RequireAuth></ShellPage>
        } />
        <Route path="/payment/:propertyId" element={
          <ShellPage><RequireAuth><PaymentPage /></RequireAuth></ShellPage>
        } />
        <Route path="/payment-success" element={
          <ShellPage><RequireAuth><PaymentSuccessPage /></RequireAuth></ShellPage>
        } />
        {/* Paystack redirects back here unauthenticated-looking (fresh tab) — never gate the callback itself */}
        <Route path="/payment/callback" element={<ShellPage><PaystackCallbackPage /></ShellPage>} />

        {/* 404 — must be last */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider theme={dwelloTheme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <MessagingProvider>
            <NotificationProvider>
              <ReviewProvider>
                <MapProvider>
                  <PaymentProvider>
                    <VendorProvider>
                      <UserActionsProvider>
                        <AppContent />
                      </UserActionsProvider>
                    </VendorProvider>
                  </PaymentProvider>
                </MapProvider>
              </ReviewProvider>
            </NotificationProvider>
          </MessagingProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
